import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button, type ButtonProps } from "./button";
import { cn } from "@/lib/utils";

/** The one tooltip look of the sidebar (mockup `#tip`): raised surface, hairline, 12 px. */
export const TIP_CLASS =
  "z-[var(--z-dropdown)] max-w-64 rounded border border-line bg-panel-2 px-2 py-0.5 text-meta text-fg shadow-lg";

/**
 * A hint shown on hover AND on keyboard focus (П3) — the words of an icon, a decoration or a cut name.
 * `children` must be one focusable element (or one that takes `tabIndex`); the hint never replaces its aria-label.
 */
export function Hint({
  text,
  children,
  side = "top",
  delay = 250,
}: {
  readonly text: React.ReactNode;
  readonly children: React.ReactElement;
  readonly side?: "top" | "bottom" | "left" | "right";
  readonly delay?: number;
}): React.JSX.Element {
  if (text === undefined || text === null || text === "") return children;
  return (
    <TooltipPrimitive.Provider delayDuration={delay} skipDelayDuration={150}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content side={side} sideOffset={4} collisionPadding={8} className={TIP_CLASS}>
            {text}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, "size" | "variant" | "children"> {
  /** The words of the action: aria-label AND the hint on hover / focus (П3). */
  readonly label: string;
  /** The icon (a lucide icon element, 14 px). */
  readonly icon: React.ReactNode;
  /** `md` — the 28x26 `.icon` of a header (П0/П1); `sm` — 24x22 inside a 28 px row (П2). */
  readonly size?: "md" | "sm";
  /** Colour role: `ok` (publish / accept), `accent` (the one action of the surface). */
  readonly tone?: "plain" | "ok" | "accent";
  /** No hint (the label is already said next to the icon, or a menu trigger opens on the same hover). */
  readonly noHint?: boolean;
  /** The hint when it says more than the label (the label stays the action's name): «Опубликовать — игроки увидят». */
  readonly hint?: string;
}

/**
 * An action that is an icon: `ICON` 28x26 (header) or 24x22 (row), no chrome until hover, the label in a hint on hover
 * and on focus, the same words in aria-label. One of the two (≤2) icons of a header, or the main icon of a row.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, icon, size = "md", tone = "plain", noHint = false, hint, className, type = "button", ...props }, ref) => {
    const button = (
      <Button
        ref={ref}
        type={type}
        variant="quiet"
        size={size === "md" ? "icon" : "iconSm"}
        aria-label={label}
        className={cn(
          "flex-none",
          tone === "ok" && "text-ok hover:bg-ok/13 hover:text-ok",
          tone === "accent" && "text-hl hover:text-hl",
          className,
        )}
        {...props}
      >
        {icon}
      </Button>
    );
    return noHint ? button : <Hint text={hint ?? label}>{button}</Hint>;
  },
);
IconButton.displayName = "IconButton";
